import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationManager = ({ variant = 'default' }) => {
  const { language } = useLanguage();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const text = {
    en: {
      enableAlerts: 'Enable Alerts',
      alertsEnabled: 'Alerts Enabled',
      disable: 'Disable',
      notSupported: 'Push notifications not supported',
      modalTitle: 'Get Instant Alerts',
      modalDesc: 'Receive immediate notifications when enforcement activity is reported in the Twin Cities. Stay informed, stay safe.',
      enableBtn: 'Enable Notifications',
      maybeLater: 'Maybe Later',
      successEnabled: 'You will now receive alerts for new enforcement activity reports.',
      successDisabled: 'Push notifications disabled. You can re-enable anytime.',
      errorPermission: 'Notification permission denied. Please enable in browser settings.',
      errorGeneric: 'Failed to enable notifications. Please try again.'
    },
    es: {
      enableAlerts: 'Activar Alertas',
      alertsEnabled: 'Alertas Activadas',
      disable: 'Desactivar',
      notSupported: 'Notificaciones push no soportadas',
      modalTitle: 'Recibe Alertas Instantáneas',
      modalDesc: 'Recibe notificaciones inmediatas cuando se reporte actividad de aplicación en las Ciudades Gemelas. Mantente informado, mantente seguro.',
      enableBtn: 'Activar Notificaciones',
      maybeLater: 'Quizás Luego',
      successEnabled: 'Ahora recibirás alertas de nuevos reportes de actividad.',
      successDisabled: 'Notificaciones push desactivadas. Puedes reactivarlas cuando quieras.',
      errorPermission: 'Permiso de notificación denegado. Por favor habilita en configuración del navegador.',
      errorGeneric: 'Error al activar notificaciones. Por favor intenta de nuevo.'
    }
  };

  const t = text[language] || text.en;

  useEffect(() => {
    // Check if push notifications are supported
    const checkSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setIsSupported(true);
        
        // Check if already subscribed
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      }
    };
    
    checkSupport();
  }, []);

  const subscribe = async () => {
    setIsLoading(true);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        toast.error(t.errorPermission);
        setIsLoading(false);
        setShowModal(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to backend
      const subscriptionData = subscription.toJSON();
      await axios.post(`${BACKEND_URL}/api/push/subscribe`, {
        endpoint: subscriptionData.endpoint,
        keys: subscriptionData.keys,
        neighborhoods: [] // Empty = all neighborhoods
      });

      setIsSubscribed(true);
      toast.success(t.successEnabled);
      setShowModal(false);
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error(t.errorGeneric);
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();
        
        // Notify backend
        await axios.post(`${BACKEND_URL}/api/push/unsubscribe`, null, {
          params: { endpoint: subscription.endpoint }
        });
      }

      setIsSubscribed(false);
      toast.success(t.successDisabled);
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error(t.errorGeneric);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  // Compact variant for header/navigation
  if (variant === 'compact') {
    return (
      <button
        onClick={() => isSubscribed ? unsubscribe() : setShowModal(true)}
        disabled={isLoading}
        className={`p-2 rounded-lg transition-all duration-300 ${
          isSubscribed 
            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
            : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
        }`}
        title={isSubscribed ? t.alertsEnabled : t.enableAlerts}
        data-testid="push-notification-toggle"
      >
        {isSubscribed ? <Bell size={18} className="fill-current" /> : <BellOff size={18} />}
      </button>
    );
  }

  return (
    <>
      {/* Main Button */}
      <button
        onClick={() => isSubscribed ? unsubscribe() : setShowModal(true)}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
          isSubscribed 
            ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300' 
            : 'bg-amber-500 text-white hover:bg-amber-600'
        }`}
        data-testid="push-notification-btn"
      >
        {isLoading ? (
          <span className="animate-spin">⏳</span>
        ) : isSubscribed ? (
          <>
            <Bell size={18} className="fill-current" />
            <span>{t.alertsEnabled}</span>
          </>
        ) : (
          <>
            <Bell size={18} />
            <span>{t.enableAlerts}</span>
          </>
        )}
      </button>

      {/* Enable Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-600"
            >
              <X size={24} />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="text-amber-600" size={32} />
              </div>
              
              <h3 className="text-xl font-bold text-stone-800 mb-2">{t.modalTitle}</h3>
              <p className="text-stone-600 mb-6">{t.modalDesc}</p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={subscribe}
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <span className="animate-spin">⏳</span>
                  ) : (
                    <>
                      <Bell size={18} />
                      {t.enableBtn}
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full py-3 px-4 text-stone-600 hover:text-stone-800 font-medium transition-colors"
                >
                  {t.maybeLater}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PushNotificationManager;
